import { Injectable } from '@angular/core';

import { createEffect, Actions, ofType } from '@ngrx/effects';
import { combineLatest, iif, Observable, of } from 'rxjs';
import { switchMap, withLatestFrom, tap, map, catchError } from 'rxjs/operators';
import { Store } from '@ngrx/store';

import { EntitiesListService } from '@apps/auth/domain/entities-list.service';
import { AppliRoleIcon, ApplicationDefaultRole, Entity } from '@apps/auth/domain/models/back/entity';
import { IAuroraState } from '@apps/aurora.state';
import { EntitiesService, UsersListService } from '@apps/auth/domain';
import { User } from '@apps/auth/domain/models/back';
import { ApplicationsService, NotfGravity, NotificationService, NotifType, UtilsService } from '@common/services';

import { getEntitiesExternalIsDataValid, getEntitiesExternalQueryString } from './entities-external.selectors';
import { getUsersExternalQueryString } from '../../users/external/users-external.selectors';
import { BackAuthEntitiesExternalODataLoaded, BackAuthEntitiesExternalODataRequested, BackAuthEntityAddEntityFailed, BackAuthEntityAddEntityRequested, BackAuthEntityAddEntitySucceeded } from './entities-external.actions';
import { BackAuthUsersExternalODataLoaded } from '../../users/external/users-external.actions';
import { NavbarItem } from '@common/models/navbar-item';

@Injectable()
export class BackAuthEntitiesExternalEffects
{
    constructor (
        private actions: Actions,
        private entitiesListService: EntitiesListService,
        private usersListService: UsersListService,
        private store: Store<IAuroraState>,
        private applicationsService: ApplicationsService,
        private entitiesService: EntitiesService,
        private notificationService: NotificationService
    )
    { }

    backAuthEntitiesExternalODataRequested = createEffect(
        () =>
            this.actions
                .pipe(
                    ofType(BackAuthEntitiesExternalODataRequested),
                    withLatestFrom(this.store.select(getEntitiesExternalIsDataValid)),
                    switchMap(([, isDataValid]: [any, boolean]) =>
                        iif(
                            (): boolean => isDataValid,
                            of(true),
                            of(false)
                                .pipe(
                                    switchMap((): Observable<[Entity[], User[]]> =>
                                        combineLatest([
                                            this.store.select(getEntitiesExternalQueryString),
                                            this.store.select(getUsersExternalQueryString)
                                        ])
                                            .pipe(
                                                switchMap(([qs1, qs2]: [string, string]): Observable<[Entity[], User[]]> =>
                                                    combineLatest([
                                                        this.entitiesListService.getEntities(qs1),
                                                        this.usersListService.getUsers(qs2)
                                                    ])
                                                        .pipe(
                                                            tap(([entities, users]: [Entity[], User[]]): void =>
                                                            {
                                                                const publicNavbarItems: NavbarItem[] = this.applicationsService.publicNavbarItems(true);
                                                                const adminNavbarItems: NavbarItem[] = [...publicNavbarItems, ...this.applicationsService.adminNavbarItems(true)];


                                                                entities.forEach((entity: Entity): void =>
                                                                {
                                                                    if (!UtilsService.isNullOrUndefined(entity.cisoId))
                                                                    {
                                                                        entity.cisoContact = users.find((user: User): boolean =>
                                                                            user.id === entity.cisoId
                                                                        );
                                                                    }

                                                                    if (!UtilsService.isNullOrUndefined(entity.dpoId))
                                                                    {
                                                                        entity.dpoContact = users.find((user: User): boolean =>
                                                                            user.id === entity.dpoId
                                                                        );
                                                                    }

                                                                    entity.appliRoleIcons = (entity.external ? publicNavbarItems : adminNavbarItems)
                                                                        .map((ni: NavbarItem): AppliRoleIcon =>
                                                                        {
                                                                            const found: ApplicationDefaultRole = entity.applications
                                                                                ?.find((adr: ApplicationDefaultRole): boolean =>
                                                                                    ni.api === adr.application
                                                                                );
                                                                            const role: string = (found !== undefined ? found.defaultRole : 'none').toUpperCase();

                                                                            return new AppliRoleIcon(ni.id, role, ni.icon);
                                                                        });
                                                                });

                                                                this.store.dispatch(BackAuthEntitiesExternalODataLoaded({ entities }));
                                                                this.store.dispatch(BackAuthUsersExternalODataLoaded({ users }));
                                                            })
                                                        )
                                                )
                                            )
                                    ))
                        )
                    )
                ),
        { dispatch: false }
    );

    backAuthEntityAddEntityRequested = createEffect(() =>
        this.actions
            .pipe(
                ofType(BackAuthEntityAddEntityRequested),
                switchMap(({ entityName, entityShortId }) =>
                    this.entitiesService.createEntity(entityName, entityShortId)
                        .pipe(
                            map((entity: Entity) =>
                                BackAuthEntityAddEntitySucceeded(entity)
                            ),
                            catchError((error) =>
                                of(BackAuthEntityAddEntityFailed(error))
                            )
                        )
                )
            )
    );

    backAuthEntityAddEntitySucceeded = createEffect(
        () =>
            this.actions
                .pipe(
                    ofType(BackAuthEntityAddEntitySucceeded),
                    tap((entity: Entity) =>
                        this.notificationService.notify(
                            [
                                '',
                                `L'entité ${entity.name} a bien été importé`
                            ],
                            NotfGravity.success,
                            NotifType.SNACKBAR
                        )
                    )
                ),
        { dispatch: false }
    );

    backAuthEntityAddEntityFailed = createEffect(
        () =>
            this.actions
                .pipe(
                    ofType(BackAuthEntityAddEntityFailed),
                    tap(() =>
                        this.notificationService.notify(
                            [
                                '',
                                'Echec de l\'importation'
                            ],
                            NotfGravity.danger,
                            NotifType.SNACKBAR
                        )
                    )
                ),
        { dispatch: false }
    );
}
